/**
 * Which countries have sale pages (web plan W0.15, master D-11, MW8).
 *
 * DATA, not a list in the code: the backend's `locales { hasSaleEvents }`
 * says whether a published sale event runs in a country (events are managed
 * per country in the Dashboard, backend MC10). A country without events has
 * no sale pages: its home does not show the calendar block, its footer and
 * sitemap do not link `/sales`, and `/bh/en/sales` and `/bh/en/sale/…` are
 * 404s rather than another country's events under its prefix.
 *
 * Until (or unless) the answer is known (the query is in flight or failed)
 * the safe fallback applies: Pakistan keeps its sale pages as before, every
 * other country has none. The SPA reads the flags with `useSaleCountries`
 * (@/hooks/useSaleCountries), the SEO server with its cached `locales` query
 * (server/routes.mjs); both hand the parsed flags to `hasSaleEvents`.
 *
 * Pure and tiny on purpose (no event config): the footer and the home page
 * import it without pulling the sale events into the entry chunk.
 */
import type { Locale } from './locales';

/** The URL country segments (`pk`) that keep their sale pages while the flags are unknown. */
export const SALE_EVENT_FALLBACK_COUNTRIES: readonly string[] = Object.freeze(['pk']);

/** `hasSaleEvents` by URL country segment (`{ pk: true, bh: false }`). */
export type SaleCountryFlags = Readonly<Record<string, boolean>>;

/** One row of the backend's `locales` answer (the fields the flags read). */
export interface BackendLocaleRow {
  country?: string | null;
  hasSaleEvents?: boolean | null;
}

/**
 * The flags of a `locales` answer, by lowercase country (a country with
 * several languages has events when any of its locales says so). Null for
 * anything that is not a usable list, so a malformed answer is "unknown",
 * not "no events".
 */
export function parseSaleCountryFlags(rows: readonly BackendLocaleRow[] | null | undefined): SaleCountryFlags | null {
  if (!Array.isArray(rows)) return null;
  const flags: Record<string, boolean> = {};
  for (const row of rows) {
    const cc = typeof row?.country === 'string' ? row.country.trim().toLowerCase() : '';
    if (!cc || typeof row.hasSaleEvents !== 'boolean') continue;
    flags[cc] = flags[cc] === true || row.hasSaleEvents;
  }
  return Object.keys(flags).length ? Object.freeze(flags) : null;
}

/**
 * Whether `locale`'s country has sale event hubs (and so a sale calendar):
 * the backend's flag when `flags` knows the country, else the fallback
 * (Pakistan only).
 */
export function hasSaleEvents(locale: Pick<Locale, 'cc'>, flags?: SaleCountryFlags | null): boolean {
  const known = flags?.[locale.cc];
  if (typeof known === 'boolean') return known;
  return SALE_EVENT_FALLBACK_COUNTRIES.includes(locale.cc);
}

/** Whether the answer for `locale`'s country is known (else `hasSaleEvents` is only the fallback). */
export function saleFlagKnown(locale: Pick<Locale, 'cc'>, flags?: SaleCountryFlags | null): boolean {
  return typeof flags?.[locale.cc] === 'boolean';
}
